<!--
  File name: AssignUserModal.vue
  Program Description:
    - This component displays the assigned users of the selected PDBNF.
    - It allows the assignor to re-assign or assign users.
-->
<template>
  <v-dialog
    v-model="showModal"
    width="40%"
    :persistent="canAssign"
  >
    <v-card class="px-10 py-5">
      <v-card-title
        class="pa-8 pl-0 font-weight-bold text-h5"
        style="color:#162b54;"
      >
        Assigned Users
      </v-card-title>
      <v-form
        ref="form"
        @submit.prevent="confirm('Save')"
      >
        <AssignUserDropdown
          v-model="evaluator"
          :items="evaluators"
          :can-edit="!disabled && canAssign"
          :rules="[v => !!v || 'Evaluator is required']"
          label="Evaluator"
        />
        <AssignUserDropdown
          v-model="reviewer"
          :items="reviewers"
          :can-edit="!disabled && canAssign"
          :rules="[v => !!v || 'First Reviewer is required']"
          label="First Reviewer"
        />
        <AssignUserDropdown
          v-model="finalReviewer"
          :items="finalReviewers"
          :can-edit="!disabled && canAssign"
          :rules="[v => !!v || 'Final Reviewer is required']"
          label="Final Reviewer"
        />
        <v-card-actions
          v-if="isInternal"
          class="pa-0 my-5"
        >
          <v-spacer />
          <template v-if="canAssign">
            <v-btn
              id="cancel-assign-button"
              color="primary"
              text
              @click="confirm('Discard')"
            >
              Cancel
            </v-btn>
            <v-btn
              id="continue-assign-button"
              class="pl-8 pr-8"
              color="primary"
              type="submit"
            >
              Continue
            </v-btn>
          </template>
          <template v-else>
            <v-btn
              v-if="!canAssign && isAssignor"
              id="re-assign-btn"
              :disabled="disabled"
              color="primary"
              @click="canAssign = true"
            >
              <v-icon class="mr-2">
                mdi-account-plus
              </v-icon>
              Re-assign
            </v-btn>
          </template>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import crud from '@/utils/rest/crud'
import AssignUserDropdown from '@/components/AssignUserDropdown'
export default {
  name: 'AssignUserModal',
  components: {
    AssignUserDropdown
  },
  props: {
    value: Boolean,
    disabled: {
      type: Boolean,
      default: false
    },
    formItem: {
      type: Object,
      default: undefined
    },
    queuing: {
      type: Boolean,
      default: false
    },
    isPriority: {
      type: Boolean,
      dafault: false
    }
  },
  data: () => ({
    dialog: false,
    canAssign: false,
    users: [],
    evaluator: '',
    reviewer: '',
    finalReviewer: ''
  }),
  computed: {
    userRole () {
      return (this.$s && this.$s.userRole)
    },
    isAssignor () {
      return this.userRole && this.userRole.includes('assignor')
    },
    isInternal () {
      return this.userRole && !this.userRole.includes('pic')
    },
    evaluators () {
      return this.users.filter(user => user.userRole.includes('evaluator'))
    },
    reviewers () {
      return this.users.filter(user => user.userRole.includes('reviewer') && user._id !== this.finalReviewer)
    },
    finalReviewers () {
      return this.users.filter(user => user.userRole.includes('reviewer') && user._id !== this.reviewer)
    },
    showModal: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    }
  },
  watch: {
    showModal: async function (show) {
      if (show) {
        if (this.queuing) {
          this.canAssign = true
          return
        }
        this.isPriority && (this.canAssign = true)
        this.setInitialData()
      } else {
        this.canAssign = false
        this.evaluator = ''
        this.reviewer = ''
        this.finalReviewer = ''
        this.$refs.form.resetValidation()
      }
    }
  },
  created () {
    this.getUsers()
  },
  methods: {
    // sets the initial data
    setInitialData () {
      this.evaluator = this.formItem.assignedTo && this.formItem.assignedTo.evaluator._id
      this.reviewer = this.formItem.assignedTo && this.formItem.assignedTo.firstPass._id
      this.finalReviewer = this.formItem.assignedTo && this.formItem.assignedTo.final._id
    },
    // Get all users
    async getUsers () {
      const dataview = 'assignmentCount'
      const response = await crud('admin/users').list({ dataview, count: 10000 })
      this.users = response.entries
    },
    // Displays confirmation dialog
    confirm (action) {
      const isValid = action === 'Save' ? this.$refs.form.validate() : true
      if (!isValid) return
      const message = action === 'Save' ? 'Are you sure you want to save the changes' : 'Changes will not be saved. Are you sure you want to discard?'
      this.$store.dispatch('showDialog', {
        title: `${action} Changes`,
        message: message,
        buttonLabel: action === 'Save' ? 'Submit' : 'Discard',
        buttonColor: action === 'Save' ? 'success' : 'error',
        callback: () => {
          action === 'Save' ? this.submitForm() : this.showModal = false
        }
      })
    },
    // Assign or re-assign PDBNF
    async submitForm () {
      const evaluator = this.evaluator
      const firstPass = this.reviewer
      const final = this.finalReviewer
      const assignedTo = { evaluator, firstPass, final }
      const resp = await crud('assign/pdbnf').update(this.formItem._id, { assignedTo })
      if (resp.ok) {
        const dbnNum = this.formItem.dbnNumber.replace('NPC BN No. ', 'DBN No. ')
        this.$store.dispatch('showAlert', {
          message: `You have successfully assigned evaluator and reviewers for ${dbnNum}`,
          type: 'success'
        })
        this.$emit('assignUser')
      } else {
        this.$store.dispatch('showAlert', {
          message: (await resp.json()).error,
          type: 'error'
        })
      }
      this.showModal = false
    }
  }
}
</script>
